import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { Col, Modal, ModalBody, Row } from "reactstrap";
import { css } from '@emotion/react';
import PndRestApi from "src/utils/pnd/PndRestApi";
import { Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip, Box, IconButton, Divider, LinearProgress } from '@mui/material';
import { showNotification } from "src/shared/components/Notification";
import Barcode from "react-barcode";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PrintIcon from '@mui/icons-material/Print';
import AssignmentIcon from '@mui/icons-material/Assignment';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PaymentIcon from '@mui/icons-material/Payment';
import InfoIcon from '@mui/icons-material/Info';
import html2canvas from 'html2canvas';
import { ERROR_MESSAGES } from "src/utils/pnd/PndConstants";

const printStyles = css`
  @media print {
    .printContainer {
      width: 100%;
      height: 100%;
      display: block;
      background-color: white;
      color: black;
    }
    .printButton {
      display: none;
    }
  }
`;

const OrderDetailsShippingViewDetailsModal = ({ modalShow, setModalShow, orderId, isTitle }) => {
    const [isOrderItemDetails, setOrderItemDetails] = useState([]);
    const [barcodePngs, setBarcodePngs] = useState([]);
    const [dataInfo, setDataInfo] = useState(null);
    const [loading, setLoading] = useState(false);
    const svgRefs = useRef([]);

    useEffect(() => {
        if (modalShow && orderId) {
            fetchAllData();
        }
    }, [modalShow, orderId]);

    const fetchAllData = async () => {
        setLoading(true);
        try {
            await Promise.all([
                getOrderByIdDetails(),
                getOrderItemWiseDetails()
            ]);
        } finally {
            setLoading(false);
        }
    };

    const getOrderByIdDetails = async () => {
        try {
            const response = await PndRestApi.getOrderByIdById(orderId);
            if (response.status === 200 && response.data.status === 200) {
                setDataInfo(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching order info:', error);
            showNotification("theme-light", "ltr", "500", ERROR_MESSAGES.tryCatchError);
        }
    };

    const getOrderItemWiseDetails = async () => {
        try {
            const response = await PndRestApi.GetOrderItemWiseDetails(orderId);
            if (response.status === 200 && response.data.status === 200) {
                setOrderItemDetails(response?.data?.data || []);
            }
        } catch (error) {
            console.error('Error fetching item details:', error);
        }
    };

    useEffect(() => {
        if (isOrderItemDetails.length > 0) {
            fetchBarcodes();
        }
    }, [isOrderItemDetails]);

    const fetchBarcodes = async () => {
        try {
            const barcodes = await Promise.all(svgRefs.current.map(async (ref) => {
                if (!ref) return null;
                const canvas = await html2canvas(ref);
                return canvas.toDataURL('image/png');
            }));
            setBarcodePngs(barcodes.filter(Boolean));
        } catch (error) {
            console.error("Error generating barcodes:", error);
        }
    };

    const toggle = () => setModalShow(prevState => !prevState);

    const handlePrint = (item, index) => {
        const printWindow = window.open('', '_blank');
        const currentDate = new Date().toLocaleString();

        const html = `
            <html>
                <head>
                    <title>Print Label - ${dataInfo?.orderNo}</title>
                    <style>
                        body { font-family: 'Inter', sans-serif; margin: 40px; }
                        .center { text-align: center; }
                        .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin: 20px 0; }
                        .box { border: 1px solid #ddd; padding: 15px; border-radius: 8px; }
                        table { width: 100%; border-collapse: collapse; margin-top: 15px; }
                        th, td { border: 1px solid #eee; padding: 10px; text-align: left; }
                    </style>
                </head>
                <body>
                    <h2 class="center">Shipment Label</h2>
                    <h4 class="center">Order No: ${dataInfo?.orderNo}</h4>
                    <div class="grid">
                        <div class="box">
                            <b>SHIPPING FROM</b><br>
                            ${dataInfo?.merchantInfo?.merchantAddress || 'N/A'}
                        </div>
                        <div class="box">
                            <b>SHIPPING TO</b><br>
                            ${dataInfo?.address}<br>
                            Contact: ${dataInfo?.contactNo}
                        </div>
                    </div>
                    <table>
                        <thead>
                            <tr><th>Sl</th><th>Barcode</th><th>Qty</th><th>Weight</th><th>Dimensions</th></tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>1</td><td>${item.boxBarCode}</td><td>${item.noOfItems}</td>
                                <td>${item.weightKg || '--'} Kg</td><td>${item.boxDimension || '--'}</td>
                            </tr>
                        </tbody>
                    </table>
                    <div class="center" style="margin-top: 30px;">
                        <img src="${barcodePngs[index]}" style="max-width: 450px; width: 100%;" />
                    </div>
                </body>
            </html>
        `;

        printWindow.document.write(html);
        printWindow.document.close();
        setTimeout(() => printWindow.print(), 500);
    };

    const order = isOrderItemDetails[0]?.orderInfo || {};
    if (!dataInfo && loading) {
        return (
            <Modal isOpen={modalShow} toggle={toggle} centered>
                <ModalBody className="text-center p-5">
                    <LinearProgress className="mb-3" />
                    <Typography variant="body2" className="text-muted">Loading Order Details...</Typography>
                </ModalBody>
            </Modal>
        );
    }

    return (
        <Modal
            isOpen={modalShow}
            toggle={toggle}
            className="theme-light ltr-support credit-memo-modal-form-ani"
            centered
            style={{ maxWidth: '1000px' }}
        >
            <div className="modal__header border-bottom-0 pb-0">
                <button
                    className="lnr lnr-cross modal__close-btn"
                    aria-label="modal__close-btn"
                    type="button"
                    onClick={toggle}
                />
                <div className="d-flex align-items-center">
                    <AssignmentIcon className="text-primary me-2" style={{ fontSize: '1.5rem' }} />
                    <h5 className="modal__title text-uppercase fw-bold m-0" style={{ color: "#32CA71", fontSize: '1.1rem' }}>
                        {isTitle || "Order Shipment Summary"}
                        <Box
                            component="span"
                            sx={{
                                backgroundColor: order?.customerDeliveryTagMappingInfo?.customerDeliveryTagInfo?.colorCode || '#32CA71',
                                color: 'white',
                                fontSize: '0.7rem',
                                px: 1.5,
                                py: 0.5,
                                borderRadius: '12px',
                                ml: 1,
                                verticalAlign: 'middle',
                                display: 'inline-block',
                                fontWeight: 600,
                                textTransform: 'none'
                            }}
                        >
                            {order?.customerDeliveryTagMappingInfo?.customerDeliveryTagInfo?.tagName}
                        </Box>
                    </h5>
                </div>
            </div>
            <ModalBody className="p-3">
                {loading && <LinearProgress sx={{ mt: -3, mb: 2 }} />}

                <Box className="printContainer" css={printStyles}>
                    {/* Identification Card */}
                    <Paper elevation={0} className="p-3 mb-3 border rounded bg-light">
                        <Row className="g-2 text-center">
                            <Col md={3}>
                                <Typography className="text-muted small fw-bold text-uppercase">Delivery No.</Typography>
                                <Typography className="fw-bold">{dataInfo?.orderNo || '--'}</Typography>
                            </Col>
                            <Col md={3} className="border-start">
                                <Typography className="text-muted small fw-bold text-uppercase">ERP No.</Typography>
                                <Typography className="fw-bold">{dataInfo?.erpOrderNo || '--'}</Typography>
                            </Col>
                            <Col md={3} className="border-start">
                                <Typography className="text-muted small fw-bold text-uppercase">Sales/Ecom Order</Typography>
                                <Typography className="fw-bold">{dataInfo?.salesOrderNo || '--'}</Typography>
                            </Col>
                             <Col md={3} className="border-start">
                                <Typography className="text-muted small fw-bold text-uppercase">Invoice No.</Typography>
                                <Typography className="fw-bold">{dataInfo?.invoiceNo || 'N/A'}</Typography>
                            </Col>
                        </Row>
                    </Paper>

                    {/* Address & History Row */}
                    <Row className="g-3 mb-3">
                        <Col lg={4}>
                            <Paper elevation={0} className="p-3 h-100 border rounded">
                                <div className="d-flex align-items-center mb-2 text-success">
                                    <LocationOnIcon fontSize="small" className="me-1" />
                                    <Typography variant="subtitle2" className="fw-bold">Shipper Address</Typography>
                                </div>
                                <Typography variant="body2" className="text-muted small" style={{ lineHeight: 1.6 }}>
                                    {dataInfo?.merchantInfo?.merchantAddress || "N/A"}
                                </Typography>
                                <Typography variant="caption" className="d-block mt-1 fw-bold">
                                    {dataInfo?.merchantInfo?.merchantName}
                                </Typography>
                            </Paper>
                        </Col>
                        <Col lg={4}>
                            <Paper elevation={0} className="p-3 h-100 border rounded">
                                <div className="d-flex align-items-center mb-2 text-primary">
                                    <LocationOnIcon fontSize="small" className="me-1" />
                                    <Typography variant="subtitle2" className="fw-bold">Client Destination</Typography>
                                </div>
                                <Typography variant="body2" className="text-muted small mb-2" style={{ lineHeight: 1.6 }}>
                                    {dataInfo?.address || "N/A"}
                                </Typography>
                                <div className="d-flex flex-wrap gap-1">
                                    {dataInfo?.contactNo && <Chip label={dataInfo.contactNo} size="small" variant="outlined" sx={{ borderRadius: 1 }} />}
                                    {dataInfo?.name && <Chip label={dataInfo.name} size="small" variant="outlined" sx={{ borderRadius: 1 }} />}
                                </div>
                            </Paper>
                        </Col>
                        <Col lg={4}>
                            <Paper elevation={0} className="p-3 h-100 border rounded bg-slate-50">
                                <div className="d-flex align-items-center mb-2 text-warning">
                                    <CalendarTodayIcon fontSize="small" className="me-1" />
                                    <Typography variant="subtitle2" className="fw-bold">Time Information</Typography>
                                </div>
                                <div className="small text-muted">
                                    <div className="d-flex justify-content-between mb-1 pb-1 border-bottom">
                                        <span>Requested:</span> <span className="fw-bold">{dataInfo?.requestedDateTimeSlot || '--'}</span>
                                    </div>
                                    <div className="d-flex justify-content-between mb-1 pb-1 border-bottom">
                                        <span>Scheduled:</span> <span className="fw-bold">{dataInfo?.scheduledDateTimeSlot || '--'}</span>
                                    </div>
                                    <div className="d-flex justify-content-between mb-1">
                                        <span>Picked Up:</span> <span className="fw-bold">{dataInfo?.pickedUpDateTime || '--'}</span>
                                    </div>
                                    <div className="d-flex justify-content-between text-success fw-bold mt-1">
                                        <span>Delivered:</span> <span>{dataInfo?.completedDateTime || '--'}</span>
                                    </div>
                                </div>
                            </Paper>
                        </Col>
                    </Row>

                    {/* Table */}
                    <Typography variant="subtitle2" className="fw-bold mb-2">Item Manifest</Typography>
                    <TableContainer component={Paper} elevation={0} className="border rounded mb-3 overflow-hidden">
                        <Table size="small">
                            <TableHead sx={{ bgcolor: '#f8f9fa' }}>
                                <TableRow>
                                    <TableCell className="fw-bold">#</TableCell>
                                    <TableCell className="fw-bold">Barcode</TableCell>
                                    <TableCell className="fw-bold">Qty</TableCell>
                                    <TableCell className="fw-bold">Dim/Wt</TableCell>
                                    <TableCell className="fw-bold text-center">Status</TableCell>
                                    <TableCell className="fw-bold">Preview</TableCell>
                                    <TableCell className="fw-bold text-center">Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isOrderItemDetails.map((item, index) => (
                                    <TableRow key={index} hover>
                                        <TableCell>{index + 1}</TableCell>
                                        <TableCell className="fw-bold text-primary">{item.boxBarCode}</TableCell>
                                        <TableCell>{item.noOfItems}</TableCell>
                                        <TableCell className="small">
                                            {item.weightKg ? `${item.weightKg}Kg` : '--'}<br />
                                            {item.boxDimension || '--'}
                                        </TableCell>
                                        <TableCell className="text-center">
                                            <Box className="d-flex justify-content-center gap-1">
                                                {item.isScannedInn ? <CheckCircleIcon color="success" sx={{ fontSize: 16 }} /> : <CancelIcon color="error" sx={{ fontSize: 16 }} />}
                                                {item.isScannedOut ? <CheckCircleIcon color="success" sx={{ fontSize: 16 }} /> : <CancelIcon color="error" sx={{ fontSize: 16 }} />}
                                            </Box>
                                        </TableCell>
                                        <TableCell className="py-1">
                                            <div ref={(el) => (svgRefs.current[index] = el)} style={{ background: 'white', padding: '5px' }}>
                                                <Barcode value={item.boxBarCode} width={1.8} height={40} fontSize={10} margin={0} />
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-center">
                                            <IconButton size="small" color="primary" onClick={() => handlePrint(item, index)}>
                                                <PrintIcon sx={{ fontSize: 18 }} />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Footer Row */}
                    <Row className="g-3">
                        <Col lg={7}>
                            <Paper elevation={0} className="p-3 border rounded">
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, color: 'info.main' }}>
                                    <PaymentIcon fontSize="small" className="me-1" />
                                    <Typography variant="subtitle2" className="fw-bold">Billing Details</Typography>
                                </Box>
                                <div className="small bg-light p-2 rounded">
                                    <div className="d-flex justify-content-between mb-1">
                                        <span className="text-muted">Payment Mode:</span>
                                        <span className="fw-bold">{dataInfo?.paymentMethodInfo?.description || '--'}</span>
                                    </div>
                                    <div className="d-flex justify-content-between mb-2">
                                        <span className="text-muted">Charge Bearer:</span>
                                        <span className="fw-bold">{dataInfo?.deliveryChargeBearerInfo?.deliveryChargeBearerName || '--'}</span>
                                    </div>
                                    <Divider className="my-1" />
                                    <div className="d-flex justify-content-between text-primary mt-1 fw-bold">
                                        <span>TOTAL TO COLLECT:</span>
                                        <Typography variant="subtitle2" className="fw-bold">{dataInfo?.riderCollectionAmount || '0.00'}</Typography>
                                    </div>
                                </div>
                            </Paper>
                        </Col>
                        <Col lg={5}>
                            <Paper elevation={0} className="p-3 h-100 border rounded bg-light">
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <InfoIcon fontSize="small" className="me-1 opacity-50" />
                                    <Typography variant="subtitle2" className="fw-bold">Notes</Typography>
                                </Box>
                                <Typography variant="caption" className="text-muted italic">
                                    {dataInfo?.orderInstruction || "No additional instructions records for this re-assigned shipment."}
                                </Typography>
                            </Paper>
                        </Col>
                    </Row>
                </Box>
            </ModalBody>
        </Modal>
    );
};

export default connect()(OrderDetailsShippingViewDetailsModal);
